import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { DataTableComponent, TableColumn, PaginationEvent } from '@shared/components/data-table/data-table.component';
import { SearchFormComponent, SearchFormData } from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent, StatusOption, ActionButton } from '@shared/components/status-filter/status-filter.component';

export interface ThirdAfterOrder {
  id: number;
  serialNo: number;
  thirdPartyOrderNo: string;
  internalOrderNo: string;
  store: string;
  channel: string;
  afterType: string;
  afterReason: string;
  platformStatus: string;
  refundStatus: string;
  amountApply: number;
  amountApproved: number;
  buyerNickname: string;
  recipientName: string;
  phoneNumber: string;
  address: string;
  applyTime: string;
  auditTime: string;
  finishTime: string;
}

@Component({
  selector: 'app-third-party-after',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DataTableComponent,
    SearchFormComponent,
    StatusFilterComponent
  ],
  templateUrl: './third-party-after.component.html',
  styleUrl: './third-party-after.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThirdPartyAfterComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private translateService = inject(TranslateService);

  // Dropdown options
  platformStatusOptions = signal<string[]>(['All', 'Pending', 'Processing', 'Completed', 'Failed']);
  refundStatusOptions = signal<string[]>(['All', 'No Refund', 'Refunded', 'Partial Refund']);
  flowStatusOptions = signal<string[]>(['All', 'Pending', 'Processing', 'Completed', 'Failed']);
  salesChannels = signal<string[]>(['All', 'Facebook', 'Shopee', 'Tmall']);

  // Table state
  selectedOrders = signal<ThirdAfterOrder[]>([]);
  loading = signal(false);
  orders = signal<ThirdAfterOrder[]>([]);
  totalRecords = signal(0);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(20);
  first = signal(0);

  // Status filter
  statusOptions: StatusOption[] = [
    { key: 'all', label: this.translateService.instant('third_party.after_sales.status.all'), active: true },
    { key: 'pending', label: this.translateService.instant('third_party.after_sales.status.pending'), active: false },
    { key: 'processing', label: this.translateService.instant('third_party.after_sales.status.processing'), active: false },
    { key: 'completed', label: this.translateService.instant('third_party.after_sales.status.completed'), active: false },
    { key: 'failed', label: this.translateService.instant('third_party.after_sales.status.failed'), active: false }
  ];

  primaryButton: ActionButton = {
    label: this.translateService.instant('third_party.after_sales.actions.create_after_sales'),
    severity: 'info',
    disabled: false,
    show: true,
    icon: 'add'
  };

  secondaryButton: ActionButton = {
    label: this.translateService.instant('third_party.after_sales.actions.download_orders'),
    severity: 'info',
    outlined: true,
    disabled: false,
    show: true,
    icon: 'download'
  };

  tableColumns: TableColumn[] = [
    { label: this.translateService.instant('third_party.after_sales.table.marketplace_order_no'), prop: 'thirdPartyOrderNo', width: 150 },
    { label: this.translateService.instant('third_party.after_sales.table.internal_order_no'), prop: 'internalOrderNo', width: 140 },
    { label: this.translateService.instant('third_party.after_sales.table.store'), prop: 'store', width: 120 },
    { label: this.translateService.instant('third_party.after_sales.table.channel'), prop: 'channel', width: 120 },
    { label: this.translateService.instant('third_party.after_sales.table.after_sales_type'), prop: 'afterType', width: 140 },
    { label: this.translateService.instant('third_party.after_sales.table.reason'), prop: 'afterReason', width: 160 },
    { label: this.translateService.instant('third_party.after_sales.table.platform_status'), prop: 'platformStatus', width: 140 },
    { label: this.translateService.instant('third_party.after_sales.table.refund_status'), prop: 'refundStatus', width: 140 },
    { label: this.translateService.instant('third_party.after_sales.table.applied_amount'), prop: 'amountApply', width: 120 },
    { label: this.translateService.instant('third_party.after_sales.table.approved_amount'), prop: 'amountApproved', width: 120 },
    { label: this.translateService.instant('third_party.after_sales.table.buyer_nickname'), prop: 'buyerNickname', width: 140 },
    { label: this.translateService.instant('third_party.after_sales.table.recipient_name'), prop: 'recipientName', width: 140 },
    { label: this.translateService.instant('third_party.after_sales.table.phone_number'), prop: 'phoneNumber', width: 140 },
    { label: this.translateService.instant('third_party.after_sales.table.address'), prop: 'address', width: 200 },
    { label: this.translateService.instant('third_party.after_sales.table.apply_time'), prop: 'applyTime', width: 160 },
    { label: this.translateService.instant('third_party.after_sales.table.audit_time'), prop: 'auditTime', width: 160 },
    { label: this.translateService.instant('third_party.after_sales.table.finish_time'), prop: 'finishTime', width: 160 }
  ];

  canCreate = computed(() => true);
  canDownload = computed(() => true);

  // Dialog state
  showCreateDialog = signal(false);
  showDownloadDialog = signal(false);

  ngOnInit(): void {
    this.loadOrders();
    this.updateButtonStates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadOrders(): void {
    this.loading.set(true);

    const mock: ThirdAfterOrder[] = [
      {
        id: 1,
        serialNo: 1,
        thirdPartyOrderNo: 'TP-AF-0001',
        internalOrderNo: 'IN-0001',
        store: 'TikTok Store',
        channel: 'TikTok',
        afterType: 'Refund',
        afterReason: 'Damaged item',
        platformStatus: 'Completed',
        refundStatus: 'Refunded',
        amountApply: 199.0,
        amountApproved: 199.0,
        buyerNickname: 'buyer123',
        recipientName: 'Alice',
        phoneNumber: '1381234',
        address: 'HCM City, Vietnam',
        applyTime: '2024-01-16 09:10:00',
        auditTime: '2024-01-16 10:00:00',
        finishTime: '2024-01-18 14:00:00'
      }
    ];

    setTimeout(() => {
      this.orders.set(mock);
      this.totalRecords.set(mock.length);
      this.loading.set(false);
    }, 400);
  }

  private updateButtonStates(): void {
    this.primaryButton.disabled = !this.canCreate();
    this.secondaryButton.disabled = !this.canDownload();
  }

  onSearch(_data: SearchFormData): void {
    this.currentPage.set(1);
    this.first.set(0);
    this.loadOrders();
  }

  onReset(): void {
    this.selectedOrders.set([]);
    this.currentPage.set(1);
    this.first.set(0);
    this.loadOrders();
    this.updateButtonStates();
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage.set((event.page || 0) + 1);
    this.first.set(event.first || 0);
    this.loadOrders();
  }

  onStatusChange(status: string): void {
    this.statusOptions = this.statusOptions.map(o => ({
      ...o,
      active: o.key === status
    }));
    this.currentPage.set(1);
    this.first.set(0);
    this.loadOrders();
  }

  onSelectionChange(selection: ThirdAfterOrder[]): void {
    this.selectedOrders.set(selection);
    this.updateButtonStates();
  }

  onCreate(): void {
    this.showCreateDialog.set(true);
  }

  onDownload(): void {
    this.showDownloadDialog.set(true);
  }

  confirmCreate(): void {
    this.showCreateDialog.set(false);
  }

  cancelCreate(): void {
    this.showCreateDialog.set(false);
  }

  confirmDownload(): void {
    this.showDownloadDialog.set(false);
  }

  cancelDownload(): void {
    this.showDownloadDialog.set(false);
  }
} 